import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva } from 'class-variance-authority';
import { type ReactNode, forwardRef } from 'react';

import { cn } from '@/lib/utils';

const plainButtonVariants = cva(
  [
    // Base styles
    'relative inline-flex items-center justify-center',
    'border-0',
    'font-semibold whitespace-nowrap',
    'transition-colors duration-150',
    'cursor-pointer disabled:cursor-not-allowed',
    'shrink-0',
    // Focus ring base
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
    'disabled:bg-gray-100! disabled:text-gray-400!',
  ],
  {
    variants: {
      color: {
        green: [
          'bg-green-400 text-gray-900',
          'hover:bg-green-500 focus:bg-green-500 focus-visible:bg-green-500',
          'focus-visible:ring-green-500',
          'active:bg-green-600',
        ],
        aqua: [
          'bg-aqua-400 text-gray-900',
          'hover:bg-aqua-500 focus:bg-aqua-500 focus-visible:bg-aqua-500',
          'focus-visible:ring-aqua-500',
          'active:bg-aqua-600',
        ],
        teal: [
          'bg-teal-400 text-gray-900',
          'hover:bg-teal-500 focus:bg-teal-500 focus-visible:bg-teal-500',
          'focus-visible:ring-teal-500',
          'active:bg-teal-600',
        ],
        violet: [
          'bg-violet-400 text-white',
          'hover:bg-violet-500 focus:bg-violet-500 focus-visible:bg-violet-500',
          'focus-visible:ring-violet-500',
          'active:bg-violet-600',
        ],
        orange: [
          'bg-orange-400 text-gray-900',
          'hover:bg-orange-500 focus:bg-orange-500 focus-visible:bg-orange-500',
          'focus-visible:ring-orange-500',
          'active:bg-orange-600',
        ],
        yellow: [
          'bg-yellow-400 text-gray-900',
          'hover:bg-yellow-500 focus:bg-yellow-500 focus-visible:bg-yellow-500',
          'focus-visible:ring-yellow-500',
          'active:bg-yellow-600',
        ],
        pink: [
          'bg-pink-400 text-gray-900',
          'hover:bg-pink-500 focus:bg-pink-500 focus-visible:bg-pink-500',
          'focus-visible:ring-pink-500',
          'active:bg-pink-600',
        ],
        blue: [
          'bg-blue-400 text-white',
          'hover:bg-blue-500 focus:bg-blue-500 focus-visible:bg-blue-500',
          'focus-visible:ring-blue-500',
          'active:bg-blue-600',
        ],
        red: [
          'bg-red-400 text-white',
          'hover:bg-red-500 focus:bg-red-500 focus-visible:bg-red-500',
          'focus-visible:ring-red-500',
          'active:bg-red-600',
        ],
        gray: [
          'bg-gray-400 text-gray-900',
          'hover:bg-gray-500 focus:bg-gray-500 focus-visible:bg-gray-500',
          'focus-visible:ring-gray-500',
          'active:bg-gray-600',
        ],
      },
      size: {
        xsmall: ['h-6 gap-1 px-1.5 py-1 text-xxs', '[&_svg]:size-3'],
        small: ['h-7 gap-1 px-1.5 py-1 text-xs leading-2.5', '[&_svg]:size-3.5'],
        medium: ['h-8 gap-1.5 px-2 py-1.5 text-xs leading-none', '[&_svg]:size-4'],
        large: ['h-10 gap-1.5 px-3.5 py-3 text-base leading-none', '[&_svg]:size-5'],
        'icon-small': ['size-6 text-xs leading-none', '[&_svg]:size-4'],
        icon: ['size-8 text-xs leading-none', '[&_svg]:size-5'],
        'icon-large': ['size-10 text-base leading-none', '[&_svg]:size-6'],
      },
      isQuiet: {
        true: 'bg-transparent',
        false: '',
      },
    },
    compoundVariants: [
      {
        size: ['xsmall', 'small', 'icon-small'],
        class: 'rounded-sm',
      },
      {
        size: ['medium', 'large', 'icon', 'icon-large'],
        class: 'rounded-md',
      },
      // Icon-only sizing
      {
        size: 'xsmall',
        class: '[&.icon-only]:w-6 [&.icon-only]:px-0 [&.icon-only_svg]:size-3',
      },
      {
        size: 'small',
        class: '[&.icon-only]:w-7 [&.icon-only]:px-0 [&.icon-only_svg]:size-4',
      },
      {
        size: 'medium',
        class: '[&.icon-only]:w-8 [&.icon-only]:px-0 [&.icon-only_svg]:size-5',
      },
      {
        size: 'large',
        class: '[&.icon-only]:w-10 [&.icon-only]:px-0 [&.icon-only_svg]:size-6',
      },
      // Invert text color for darker colors when isQuiet is true
      {
        color: 'violet',
        isQuiet: true,
        class:
          'text-gray-900 hover:text-white focus:text-white focus-visible:text-white active:text-white',
      },
      {
        color: 'blue',
        isQuiet: true,
        class:
          'text-gray-900 hover:text-white focus:text-white focus-visible:text-white active:text-white',
      },
      {
        color: 'red',
        isQuiet: true,
        class:
          'text-gray-900 hover:text-white focus:text-white focus-visible:text-white active:text-white',
      },
    ],
    defaultVariants: {
      color: 'green',
      size: 'medium',
      isQuiet: false,
    },
  },
);

export type PlainButtonProps = ButtonPrimitive.Props & {
  color?:
    | 'green'
    | 'aqua'
    | 'teal'
    | 'violet'
    | 'orange'
    | 'yellow'
    | 'pink'
    | 'blue'
    | 'red'
    | 'gray';
  size?: 'xsmall' | 'small' | 'medium' | 'large' | 'icon-small' | 'icon' | 'icon-large';
  isQuiet?: boolean;
  icon?: ReactNode;
};

const PlainButton = forwardRef<HTMLButtonElement, PlainButtonProps>(
  (
    {
      className,
      children,
      color = 'green',
      size = 'medium',
      icon,
      disabled = false,
      isQuiet = false,
      type = 'button',
      ...rest
    },
    ref,
  ) => {
    const isIconOnly = children == null && icon != null;

    return (
      <ButtonPrimitive
        data-slot="button"
        ref={ref}
        type={type}
        disabled={disabled}
        className={cn(
          plainButtonVariants({ color, size, isQuiet }),
          isIconOnly && 'icon-only',
          className,
        )}
        {...rest}
      >
        {icon}
        {children}
      </ButtonPrimitive>
    );
  },
);

PlainButton.displayName = 'PlainButton';

export default PlainButton;
